import { Button } from "@/components/ui/button";
import { X, UserCog, UserX, Shield, User } from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useState } from "react";

interface BulkUserActionsToolbarProps {
  selectedCount: number;
  onClearSelection: () => void;
  onBulkUpdateRole: (role: 'user' | 'tenant_admin') => void;
  onBulkRemove: () => void;
  isLoading?: boolean;
}

export function BulkUserActionsToolbar({
  selectedCount,
  onClearSelection,
  onBulkUpdateRole,
  onBulkRemove,
  isLoading = false,
}: BulkUserActionsToolbarProps) {
  const [showRoleDialog, setShowRoleDialog] = useState(false);
  const [showRemoveDialog, setShowRemoveDialog] = useState(false);
  const [selectedRole, setSelectedRole] = useState<'user' | 'tenant_admin'>('user');

  const handleRoleChange = (role: 'user' | 'tenant_admin') => {
    setSelectedRole(role);
    setShowRoleDialog(true);
  };

  const confirmRoleChange = () => {
    onBulkUpdateRole(selectedRole);
    setShowRoleDialog(false);
  };

  const confirmRemove = () => {
    onBulkRemove();
    setShowRemoveDialog(false);
  };

  return (
    <>
      <div className="flex items-center justify-between gap-4 p-4 bg-muted/50 border-b">
        <div className="flex items-center gap-2">
          <UserCog className="h-4 w-4" />
          <span className="text-sm font-medium">
            {selectedCount} user{selectedCount !== 1 ? 's' : ''} selected
          </span>
        </div>
        
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => handleRoleChange('user')}
            disabled={isLoading}
          >
            <User className="h-4 w-4 mr-2" />
            Set as User
          </Button>
          
          <Button
            variant="outline"
            size="sm"
            onClick={() => handleRoleChange('tenant_admin')}
            disabled={isLoading}
          >
            <Shield className="h-4 w-4 mr-2" />
            Set as Admin
          </Button>
          
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowRemoveDialog(true)}
            disabled={isLoading}
          >
            <UserX className="h-4 w-4 mr-2" />
            Remove
          </Button>
          
          <Button
            variant="ghost"
            size="sm"
            onClick={onClearSelection}
            disabled={isLoading}
          >
            <X className="h-4 w-4 mr-2" />
            Clear
          </Button>
        </div>
      </div>

      <AlertDialog open={showRoleDialog} onOpenChange={setShowRoleDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Change User Roles</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to change the role of {selectedCount} user{selectedCount !== 1 ? 's' : ''} to{' '}
              <strong>{selectedRole === 'tenant_admin' ? 'Tenant Admin' : 'User'}</strong>?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmRoleChange}>
              Confirm
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={showRemoveDialog} onOpenChange={setShowRemoveDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove Users</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to remove {selectedCount} user{selectedCount !== 1 ? 's' : ''} from this tenant?
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmRemove} className="bg-destructive hover:bg-destructive/90">
              Remove Users
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
